// redux/measurementRequest/measurementRequestActions.js
import {
    getMethod,
    putMethod,
    postMethod,
    deleteMethod
} from "../../api-service/api-service";
import {
    setRequests,
    setSelectedRequest,
    setLoading,
    setError,
    setPagination,
    setFilters,
    updateRequestInList,
    removeRequest
} from "./measurementRequestSlice";
import { toast } from "react-toastify";

// =========================================
// FETCH ALL MEASUREMENT REQUESTS (ADMIN)
// =========================================
export const fetchMeasurementRequests = (page = 1, limit = 20, filters = {}) => async (dispatch) => {
    try {
        dispatch(setLoading(true));
        dispatch(setError(null));

        // Update filters in state
        dispatch(setFilters({ ...filters, page, limit }));

        // Build query string
        const queryParams = new URLSearchParams({
            page,
            limit,
            ...filters
        }).toString();

        const response = await getMethod(`/measurement-request/admin/all?${queryParams}`);

        if (response.success) {
            dispatch(setRequests(response.data));
            if (response.pagination) {
                dispatch(setPagination(response.pagination));
            }
            return { success: true, data: response.data };
        } else {
            dispatch(setError(response.message || 'Failed to fetch requests'));
            return { success: false, message: response.message };
        }
    } catch (error) {
        const message = error?.response?.data?.message || 'Unable to fetch requests';
        dispatch(setError(message));
        toast.error(message);
        return { success: false, message };
    } finally {
        dispatch(setLoading(false));
    }
};

// =========================================
// FETCH SINGLE MEASUREMENT REQUEST (ADMIN)
// =========================================
export const fetchMeasurementRequestDetails = (id) => async (dispatch) => {
    try {
        dispatch(setLoading(true));
        dispatch(setError(null));

        const response = await getMethod(`/measurement-request/admin/${id}`);

        if (response.success) {
            dispatch(setSelectedRequest(response.data));
            return { success: true, data: response.data };
        } else {
            dispatch(setError(response.message || 'Failed to fetch request details'));
            return { success: false, message: response.message };
        }
    } catch (error) {
        const message = error?.response?.data?.message || 'Unable to fetch request details';
        dispatch(setError(message));
        toast.error(message);
        return { success: false, message };
    } finally {
        dispatch(setLoading(false));
    }
};

// =========================================
// SCHEDULE MEASUREMENT (ADMIN)
// =========================================
export const scheduleMeasurementRequest = (id, scheduleData) => async (dispatch) => {
    try {
        dispatch(setLoading(true));
        dispatch(setError(null));

        const response = await putMethod(`/measurement-request/admin/${id}/schedule`, scheduleData);

        if (response.success) {
            dispatch(updateRequestInList(response.data));
            toast.success(response.message || 'Measurement scheduled successfully');
            return { success: true, data: response.data };
        } else {
            dispatch(setError(response.message || 'Failed to schedule measurement'));
            toast.error(response.message || 'Failed to schedule measurement');
            return { success: false, message: response.message };
        }
    } catch (error) {
        const message = error?.response?.data?.message || 'Unable to schedule measurement';
        dispatch(setError(message));
        toast.error(message);
        return { success: false, message };
    } finally {
        dispatch(setLoading(false));
    }
};

// =========================================
// CANCEL MEASUREMENT REQUEST (ADMIN)
// =========================================
export const cancelMeasurementRequest = (id, cancelledReason) => async (dispatch) => {
    try {
        dispatch(setLoading(true));
        dispatch(setError(null));

        const response = await putMethod(`/measurement/admin/${id}/cancel`, { cancelled_reason: cancelledReason });

        if (response.success) {
            dispatch(updateRequestInList(response.data));
            toast.success(response.message || 'Request cancelled successfully');
            return { success: true, data: response.data };
        } else {
            dispatch(setError(response.message || 'Failed to cancel request'));
            toast.error(response.message || 'Failed to cancel request');
            return { success: false, message: response.message };
        }
    } catch (error) {
        const message = error?.response?.data?.message || 'Unable to cancel request';
        dispatch(setError(message));
        toast.error(message);
        return { success: false, message };
    } finally {
        dispatch(setLoading(false));
    }
};

// =========================================
// COMPLETE MEASUREMENT (ADMIN)
// =========================================
export const completeMeasurementRequest = (id, adminNotes) => async (dispatch) => {
    try {
        dispatch(setLoading(true));
        dispatch(setError(null));

        const response = await putMethod(`/measurement-request/admin/${id}/complete`, { admin_notes: adminNotes });

        if (response.success) {
            dispatch(updateRequestInList(response.data));
            toast.success(response.message || 'Measurement completed successfully');
            return { success: true, data: response.data };
        } else {
            dispatch(setError(response.message || 'Failed to complete measurement'));
            toast.error(response.message || 'Failed to complete measurement');
            return { success: false, message: response.message };
        }
    } catch (error) {
        const message = error?.response?.data?.message || 'Unable to complete measurement';
        dispatch(setError(message));
        toast.error(message);
        return { success: false, message };
    } finally {
        dispatch(setLoading(false));
    }
};

// =========================================
// RESCHEDULE MEASUREMENT (ADMIN)
// =========================================
export const rescheduleMeasurementRequest = (id, rescheduleData) => async (dispatch) => {
    try {
        dispatch(setLoading(true));
        dispatch(setError(null));

        const response = await putMethod(`/measurement-request/admin/${id}/reschedule`, rescheduleData);

        if (response.success) {
            dispatch(updateRequestInList(response.data));
            toast.success(response.message || 'Request rescheduled successfully');
            return { success: true, data: response.data };
        } else {
            dispatch(setError(response.message || 'Failed to reschedule request'));
            toast.error(response.message || 'Failed to reschedule request');
            return { success: false, message: response.message };
        }
    } catch (error) {
        const message = error?.response?.data?.message || 'Unable to reschedule request';
        dispatch(setError(message));
        toast.error(message);
        return { success: false, message };
    } finally {
        dispatch(setLoading(false));
    }
};

// =========================================
// DELETE MEASUREMENT REQUEST (ADMIN)
// =========================================
export const deleteMeasurementRequest = (id) => async (dispatch) => {
    try {
        dispatch(setLoading(true));
        dispatch(setError(null));

        const response = await deleteMethod(`/measurement-request/admin/${id}`);

        if (response.success) {
            dispatch(removeRequest(id));
            toast.success(response.message || 'Request deleted successfully');
            return { success: true };
        } else {
            dispatch(setError(response.message || 'Failed to delete request'));
            toast.error(response.message || 'Failed to delete request');
            return { success: false, message: response.message };
        }
    } catch (error) {
        const message = error?.response?.data?.message || 'Unable to delete request';
        dispatch(setError(message));
        toast.error(message);
        return { success: false, message };
    } finally {
        dispatch(setLoading(false));
    }
};